import { useEffect, useRef, useState } from 'react';

/**
 * The lab machine's state, and an "Open IDE" that starts an idle one (E9-S18).
 *
 * The host stops itself after 30 minutes idle to save money (F-073), and gets a new public
 * address every time it starts; it publishes that address, with the time, on every boot.
 * So opening an idle IDE is: start the machine, wait for it to run, wait for the address it
 * publishes *after* the start, wait for the IDE to answer, then open that address.
 */

export const STEPS = [
  { key: 'start', label: 'Starting the machine' },
  { key: 'address', label: 'Waiting for its new address' },
  { key: 'ide', label: 'Waiting for the IDE to answer' },
];

// Usually about two minutes; well past that, the student is told rather than left waiting.
export const TYPICAL_SECONDS = 120;
export const GIVE_UP_SECONDS = 360;
// The IDE's certificate is self-signed, so a check from this page fails until the browser
// has accepted it once. After this long with the machine running on its new address, the
// IDE is opened anyway - the certificate warning is then the expected next screen.
export const IDE_GRACE_SECONDS = 60;
const POLL_SECONDS = 5;

export const STATE_WORDS = {
  running: 'Running',
  stopped: 'Idle',
  pending: 'Starting',
  stopping: 'Stopping',
};

/** Whether the IDE at this address answers at all. Opaque is fine: it answered. */
export async function probeIde(url, timeoutMs = 4000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Start (if need be) and wait, reporting each step; resolves to the address to open.
 *
 * Everything that touches time or the network is passed in, so the sequence is testable.
 * Rejects with an Error whose `timedOut` is true when it has taken far longer than usual.
 */
export async function launchIde({ accountId, labInstance, onStep, sleep, probe, now = () => Date.now() }) {
  const began = now();
  const elapsed = () => (now() - began) / 1000;
  const giveUp = () => {
    const error = new Error('This is taking much longer than usual.');
    error.timedOut = true;
    throw error;
  };

  onStep('start');
  let info = await labInstance(accountId, 'describe');
  // A machine that is stopping must finish stopping before it can be started again.
  while (info.state === 'stopping') {
    if (elapsed() > GIVE_UP_SECONDS) giveUp();
    await sleep(POLL_SECONDS * 1000);
    info = await labInstance(accountId, 'describe');
  }
  const startedAt = new Date(now()).toISOString();
  const wasRunning = info.state === 'running';
  if (info.state === 'stopped') info = await labInstance(accountId, 'start');

  while (info.state !== 'running') {
    if (elapsed() > GIVE_UP_SECONDS) giveUp();
    await sleep(POLL_SECONDS * 1000);
    info = await labInstance(accountId, 'describe');
  }

  onStep('address');
  // A machine that was already running keeps its address. One that was started publishes a
  // new one on boot, stamped after the start - until then the table still has the old one.
  while (!wasRunning && !(info.codeServerUrl && info.hostUpdatedAt && info.hostUpdatedAt > startedAt)) {
    if (elapsed() > GIVE_UP_SECONDS) giveUp();
    await sleep(POLL_SECONDS * 1000);
    info = await labInstance(accountId, 'describe');
  }
  const url = info.codeServerUrl;
  if (!url) giveUp();

  onStep('ide');
  const ideSince = now();
  while (!(await probe(url))) {
    if ((now() - ideSince) / 1000 > IDE_GRACE_SECONDS) break;
    await sleep(POLL_SECONDS * 1000);
  }
  return url;
}

const sleepFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The IDE button (owner, 2026-09-26). Inactive until the machine's state is known. On an
 * idle machine it says Start IDE: starting follows the machine to running on its new address
 * with the IDE answering, and the button then says Open IDE. Opening is always a click of its
 * own, so the browser never holds the tab back.
 */
function IdeLauncher({ lab, labInstance }) {
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const [ideUrl, setIdeUrl] = useState(lab.codeServerUrl);
  const [starting, setStarting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const check = () => {
    if (!labInstance) return;
    setState(null);
    labInstance(lab.accountId, 'describe')
      .then((info) => {
        if (!mounted.current) return;
        // The address as the host last published it. The one the dialog was given is from
        // when the page loaded, and a machine that has restarted since has a new one - an
        // Open IDE on the old address goes nowhere (found 2026-09-26, ServerlessCiCd).
        if (info.codeServerUrl) setIdeUrl(info.codeServerUrl);
        setState(info.state);
      })
      .catch(() => mounted.current && setState('unknown'));
  };

  // The dialog shows the details at once; the button waits for the state.
  useEffect(() => {
    if (lab.codeServerUrl) check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab.accountId, lab.codeServerUrl, labInstance]);

  useEffect(() => {
    if (!starting) return undefined;
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [starting]);

  async function start() {
    setError(null);
    setSeconds(0);
    setStarting(true);
    try {
      const url = await launchIde({
        accountId: lab.accountId,
        labInstance,
        onStep: (key) => mounted.current && setStep(key),
        sleep: sleepFor,
        probe: probeIde,
      });
      if (!mounted.current) return;
      setIdeUrl(url);
      setState('running');
    } catch (failure) {
      if (mounted.current) setError(failure.timedOut ? failure.message : 'The machine could not be started.');
    } finally {
      if (mounted.current) {
        setStarting(false);
        setStep(null);
      }
    }
  }

  const open = () => window.open(ideUrl, '_blank', 'noopener,noreferrer');
  const known = state !== null && state !== 'unknown' && state !== 'none';
  const running = state === 'running';
  const label = starting ? 'Starting your IDE…' : running ? 'Open IDE' : known ? 'Start IDE' : 'Open IDE';
  const index = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="ide-launcher">
      <p className={`ide-state ide-state-${state || 'checking'}`} role="status">
        <span className="ide-state-dot" aria-hidden="true" /> Machine:{' '}
        {state === null ? 'checking…' : STATE_WORDS[state] || 'could not be checked'}
      </p>
      <button
        type="button"
        className="lab-dialog-signin ide-open"
        onClick={running ? open : start}
        disabled={!known || starting}
      >
        {label}
      </button>

      {starting && (
        <div className="ide-progress" role="progressbar" aria-busy="true" aria-label="Starting your IDE">
          <ol>
            {STEPS.map((s, i) => (
              <li key={s.key} className={i < index ? 'is-done' : i === index ? 'is-current' : 'is-waiting'}>
                <span className="ide-step-mark" aria-hidden="true">
                  {i < index ? '✓' : i === index ? <span className="ide-spinner" /> : '·'}
                </span>
                {s.label}
              </li>
            ))}
          </ol>
          <p className="lab-access-note">
            {seconds}s — usually about {Math.round(TYPICAL_SECONDS / 60)} minutes.
            {seconds > TYPICAL_SECONDS && ' Taking a little longer than usual; still working.'}
          </p>
        </div>
      )}

      {state === 'unknown' && (
        <p className="lab-access-note ide-error">
          The machine could not be checked.{' '}
          <button type="button" className="lab-access-button" onClick={check}>
            Check again
          </button>
        </p>
      )}

      {error && (
        <p className="lab-access-note ide-error" role="alert">
          {error}{' '}
          <button type="button" className="lab-access-button" onClick={start}>
            Try again
          </button>
        </p>
      )}
    </div>
  );
}

export default IdeLauncher;
